import React, { useLayoutEffect, useRef, useState } from 'react';
import Container from './Container';
import AnimatedWords from './AnimatedWords';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const years = [2020, 2021, 2022, 2023, 2024, 2025];

// Helper function to calculate opacity based on distance from active year
const calculateOpacity = (year, activeYearValue, yearsArray) => {
  const maxDistance = Math.max(...yearsArray.map(y => Math.abs(y - activeYearValue)));
  const distance = Math.abs(year - activeYearValue);
  return maxDistance > 0 ? 1 - (distance / maxDistance) * 0.7 : 1;
};

const AboutTimeline = () => {
  const [activeYear, setActiveYear] = useState(2020);
  const activeYearRef = useRef(2020);
  const sectionRef = useRef(null);
  const pinTlRef = useRef(null);

  const timelineData = {
    2020: {
      title: 'The Beginning',
      description: "Timeline Express LLP was founded with a vision to deliver goods 'On Time Everywhere.' We started with by-air express services, catering to businesses that needed speed and reliability above all.",
      image: '/images/the_beginning.webp',
      imageAlt: 'Timeline Express Founding Team',
    },
    2021: {
      title: 'Expanding Horizons',
      description: 'Recognizing the growing demand for cost-effective and sustainable logistics, we introduced rail cargo solutions, ensuring faster movement with reduced costs.',
      image: '/images/expanding_horizons.webp',
      imageAlt: 'Expanding Horizons',
    },
    2022: {
      title: 'Taking to the Roads',
      description: 'We entered into surface logistics, offering both LTL (Less Than Truckload) and FTL (Full Truckload) services, building a strong network across key trade routes in India.',
      image: '/images/taking_to_the_roads.webp',
      imageAlt: 'Taking to the Roads',
    },
    2023: {
      title: 'Technology & Transparency',
      description: 'We invested in tech-driven tracking and operations, giving our clients real-time shipment visibility and complete transparency in freight management.',
      image: '/images/technology_transparency.webp',
      imageAlt: 'Technology and Transparency',
    },
    2024: {
      title: 'Scaling Up',
      description: 'Our fleet and partner network grew significantly, allowing us to serve more industries with customized logistics solutions across air, rail, and road.',
      image: '/images/scaling_up.webp',
      imageAlt: 'Scaling Up',
    },
    2025: {
      title: 'The Road Ahead',
      description: "With a strong presence in multi-modal logistics, we are working towards becoming one of India's most trusted logistics partners, connecting businesses nationwide with speed, reliability, and innovation.",
      image: '/images/road_ahead.webp',
      imageAlt: 'The Road Ahead',
    },
  };

  const currentData = timelineData[activeYear];

  const commitActiveYear = (year) => {
    if (activeYearRef.current !== year) {
      activeYearRef.current = year;
      setActiveYear(year);
    }
  };

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Heading animation (run once)
      const headingWords = sectionRef.current?.querySelectorAll('.timeline-header h2 [data-animated-word]');
      if (headingWords && headingWords.length) {
        gsap.fromTo(headingWords, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06, clearProps: 'transform', scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', toggleActions: 'play none none none', once: true } });
      }

      const yearEls = gsap.utils.toArray('.timeline-years .year-item');

      // Pin the whole section and scrub through years with softer snapping
      const totalSteps = years.length - 1; // transitions count

      const updateUI = (year) => {
        yearEls.forEach((el, i) => {
          const currentYear = years[i];
          const isActive = currentYear === year;

          // Calculate opacity based on distance from active year
          const opacity = calculateOpacity(currentYear, year, years);

          // Apply opacity to the year item
          gsap.to(el, {
            opacity: opacity,
            duration: 0.3,
            ease: 'power2.out'
          });

          const line = el.querySelector('.timeline-line');
          if (line) {
            gsap.to(line, {
              scaleX: isActive ? 1 : 0,
              opacity: isActive ? 1 : 0,
              duration: 0.25,
              ease: 'power2.out'
            });
          }
        });
      };

      const pinTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: () => `+=${window.innerHeight * 1.5}`,
          pin: true,
          scrub: 0.7,
          snap: {
            snapTo: (value) => {
              if (totalSteps <= 0) return 0;
              return Math.round(value * totalSteps) / totalSteps;
            },
            duration: 0.45,
            ease: 'power2.out',
            inertia: false
          },
          onEnterBack: () => {
            commitActiveYear(years[0]);
            updateUI(years[0]);
          },
          onUpdate: (self) => {
            const p = self.progress; // 0..1
            const idx = Math.round(p * totalSteps);
            const clampedIdx = Math.min(Math.max(idx, 0), totalSteps);
            const year = years[clampedIdx];
            if (activeYearRef.current !== year) commitActiveYear(year);
            updateUI(year);
          }
        }
      });
      pinTlRef.current = pinTl;

      // Dummy tween for progress
      pinTl.to({}, { duration: totalSteps || 1 });

      // Initial UI state
      updateUI(activeYearRef.current);
    }, sectionRef);

    return () => ctx.revert();
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animateToYear = (year) => {
    const tl = pinTlRef.current;
    if (!tl || !tl.scrollTrigger) return commitActiveYear(year);
    const st = tl.scrollTrigger;
    const totalSteps = years.length - 1;
    const idx = Math.max(0, Math.min(years.indexOf(year), totalSteps));
    const p = totalSteps > 0 ? idx / totalSteps : 0;
    const target = st.start + (st.end - st.start) * p;
    const proxy = { v: st.scroll() };
    gsap.to(proxy, {
      v: target,
      duration: 0.6,
      ease: 'power2.out',
      onUpdate: () => st.scroll(proxy.v)
    });
  };

  return (
    <section
      id="journey"
      className={`about-timeline section alt`}
      ref={sectionRef}
    >
      <Container>
        <div className="timeline-frame">
          <div className="timeline-header">
            <div className="container section-badge flex items-center gap-3 !text-[16px] !md:text-[20px] !px-1">
              <div className="badge-dot"></div>
              Our Story
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl">
              <AnimatedWords auto={false} lines={["The Timeline of Our Growth"]} accentMap={[[1]]} />
            </h2>
          </div>

          <div className="timeline-content">
            <div className="timeline-sidebar">
              <div className="timeline-years">
                {years.map((year) => {
                  const isActive = activeYear === year;
                  const opacity = calculateOpacity(year, activeYear, years);
                  return (
                    <div
                      key={year}
                      className={`year-item ${isActive ? 'active' : ''}`}
                      style={{ opacity }}
                      onClick={() => animateToYear(year)}
                    >
                      <div className="timeline-label flex items-center gap-2" aria-hidden={!isActive}>
                        <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 border-black 'bg-black/10'  `}>
                          <div className="w-2.5 h-2.5 bg-black rounded-full" />
                        </div>
                        <span>YEAR</span>
                      </div>
                      <div className="timeline-line" aria-hidden="true"></div>
                      <span className="year-text text-[18px] md:text-[24px]">{year}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="timeline-details" style={{ opacity: 1, transform: 'translateY(0px)' }}>
              <h3 className="text-xl md:text-2xl lg:text-3xl">{currentData.title}</h3>
              <p className="text-sm md:text-base lg:text-lg">{currentData.description}</p>
              <div className="timeline-image">
                <img src={currentData.image} alt={currentData.imageAlt} loading="lazy" decoding="async" />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default AboutTimeline;